/**
 * Web platform bindings for `@slumbr/core`.
 *
 * Side-effect on import: calls `configurePlatform` immediately so any
 * subsequently-imported `@slumbr/core` module (theme store hydration, API
 * client, Cognito client) sees the adapter. `main.tsx` must import this
 * BEFORE importing any module that pulls `@slumbr/core`.
 *
 * Theme store hydration is explicit (skipHydration: true in
 * theme-store.ts) — we kick it manually below AFTER configurePlatform()
 * runs, so persisted theme actually reads from real localStorage.
 */
import {
  configurePlatform,
  useThemeStore,
  type StorageAdapter,
} from "@slumbr/core";

const webStorage: StorageAdapter = {
  getItem: (key) => window.localStorage.getItem(key),
  setItem: (key, value) => {
    window.localStorage.setItem(key, value);
  },
  removeItem: (key) => {
    window.localStorage.removeItem(key);
  },
};

configurePlatform({
  storage: webStorage,
  getEnvVar: (key, fallback) => {
    const value = (import.meta.env as Record<string, string | undefined>)[key];
    return value ?? fallback;
  },
  onUnauthorized: () => {
    // Hard redirect so any in-flight React tree tears down cleanly.
    window.location.href = "/login";
  },
  prefersDark: () =>
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches,
});

// Now that real storage is wired, ask the theme store to read its
// persisted value. Fire-and-forget — failure here just means we render
// the default theme until next user-driven setMode() call.
void useThemeStore.persist.rehydrate();
